import { useState, useRef, useEffect } from "react";

// Функция для безопасного создания URL из файла
const createSafeObjectURL = (file) => {
  try {
    if (file && file instanceof File) {
      return URL.createObjectURL(file);
    }
    return null;
  } catch (error) {
    console.error('Error creating object URL:', error);
    return null;
  }
};

export function useGroupMaterials({ groupData, setGroupData, showCustomModalConfig, setShowCustomModal }) {
  const [newMaterialLink, setNewMaterialLink] = useState('');
  const [newMaterialFile, setNewMaterialFile] = useState(null);
  const [materialType, setMaterialType] = useState('file'); // 'file' или 'link'
  const materialFileInputRef = useRef(null);
  const [materialSearchTerm, setMaterialSearchTerm] = useState('');
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [editingMaterialName, setEditingMaterialName] = useState('');
  const [previewMaterial, setPreviewMaterial] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleAddLink = () => {
    if (newMaterialLink.trim()) {
      const today = new Date();
      const dateOfUpload = `${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getDate().toString().padStart(2, '0')}/${today.getFullYear()}`;
      const url = newMaterialLink.trim();
      const name = url.length > 50 ? url.substring(0, 47) + '...' : url;
      setGroupData(prev => ({
        ...prev,
        materials: [...prev.materials, { type: 'link', name: name, value: url, dateOfUpload: dateOfUpload }]
      }));
      setNewMaterialLink('');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewMaterialFile(file);
      // Автоматически добавляем файл
      const today = new Date();
      const dateOfUpload = `${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getDate().toString().padStart(2, '0')}/${today.getFullYear()}`;

      // Создаем URL для файла
      const fileUrl = createSafeObjectURL(file);

      setGroupData(prev => ({
        ...prev,
        materials: [...prev.materials, {
          type: 'file',
          name: file.name,
          value: fileUrl,
          file: file,
          extension: file.name.split('.').pop().toLowerCase(),
          dateOfUpload: dateOfUpload
        }]
      }));

      // Очищаем input
      if (materialFileInputRef.current) {
        materialFileInputRef.current.value = "";
      }
      setNewMaterialFile(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      setNewMaterialFile(file);
      // Автоматически добавляем файл
      const today = new Date();
      const dateOfUpload = `${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getDate().toString().padStart(2, '0')}/${today.getFullYear()}`;

      // Создаем URL для файла
      const fileUrl = createSafeObjectURL(file);

      setGroupData(prev => ({
        ...prev,
        materials: [...prev.materials, {
          type: 'file',
          name: file.name,
          value: fileUrl,
          file: file,
          extension: file.name.split('.').pop().toLowerCase(),
          dateOfUpload: dateOfUpload
        }]
      }));

      setNewMaterialFile(null);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Проверяем, что мы действительно покидаем элемент, а не переходим к дочернему
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAddFile = () => {
    if (newMaterialFile) {
      const today = new Date();
      const dateOfUpload = `${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getDate().toString().padStart(2, '0')}/${today.getFullYear()}`;

      // Создаем URL для файла
      const fileUrl = createSafeObjectURL(newMaterialFile);

      setGroupData(prev => ({
        ...prev,
        materials: [...prev.materials, {
          type: 'file',
          name: newMaterialFile.name,
          value: fileUrl,
          file: newMaterialFile,
          extension: newMaterialFile.name.split('.').pop().toLowerCase(),
          dateOfUpload: dateOfUpload
        }]
      }));
      setNewMaterialFile(null);
      if (materialFileInputRef.current) {
        materialFileInputRef.current.value = "";
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAddMaterial = () => {
    if (materialType === 'link') {
      handleAddLink();
    }
  };



  const handleDeleteMaterial = (index) => {
    showCustomModalConfig({
      title: 'Delete Material',
      message: `Are you sure you want to delete "${groupData.materials[index].name}"? This action cannot be undone.`,
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: () => {
        const updatedMaterials = [...groupData.materials];
        updatedMaterials.splice(index, 1);
        setGroupData(prev => ({
          ...prev,
          materials: updatedMaterials
        }));
        setShowCustomModal(false);
      }
    });
  };

  const handleEditMaterialName = (index) => {
    setEditingMaterialId(index);
    setEditingMaterialName(groupData.materials[index].name);
  };

  const handleSaveMaterialName = (index) => {
    if (editingMaterialName.trim()) {
      const material = groupData.materials[index];
      let newName = editingMaterialName.trim();

      // Для файлов автоматически добавляем расширение, если его нет
      if (material.type === 'file' && material.extension) {
        const hasExtension = newName.includes('.');
        if (!hasExtension) {
          newName = newName + '.' + material.extension;
        } else {
          // Проверяем, что расширение файла не изменилось
          const newExtension = newName.split('.').pop().toLowerCase();
          if (newExtension !== material.extension.toLowerCase()) {
            return;
          }
        }
      }

      const updatedMaterials = [...groupData.materials];
      updatedMaterials[index] = {
        ...updatedMaterials[index],
        name: newName
      };
    setGroupData(prev => ({
      ...prev,
        materials: updatedMaterials
      }));
    }
    setEditingMaterialId(null);
    setEditingMaterialName('');
  };

  const handleCancelEditMaterial = () => {
    setEditingMaterialId(null);
    setEditingMaterialName('');
  };

  const handleMaterialPreview = (material) => {
    // Для ссылок открываем сразу в новой вкладке
    if (material.type === 'link') {
      window.open(material.value, '_blank', 'noopener,noreferrer');
      return;
    }
    setPreviewMaterial(material);
  };

  const handleClosePreview = () => {
    setPreviewMaterial(null);
  };

  // Автоматическая прокрутка к модальному окну превью и блокировка прокрутки страницы
  useEffect(() => {
    if (previewMaterial) {
      // Блокируем прокрутку страницы
      document.body.style.overflow = 'hidden';

      const modalElement = document.querySelector('.preview-modal');
      if (modalElement) {
        modalElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    } else {
      // Восстанавливаем прокрутку страницы
      document.body.style.overflow = 'auto';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [previewMaterial]);

  const filteredMaterials = groupData.materials?.filter(material =>
    (material.name || '').toLowerCase().includes(materialSearchTerm.toLowerCase())
  ) || [];

  return {
    newMaterialLink, setNewMaterialLink,
    newMaterialFile, setNewMaterialFile,
    materialType, setMaterialType,
    materialFileInputRef,
    materialSearchTerm, setMaterialSearchTerm,
    editingMaterialId, setEditingMaterialId,
    editingMaterialName, setEditingMaterialName,
    previewMaterial, setPreviewMaterial,
    isDragOver,
    filteredMaterials,
    handleAddLink, handleFileChange,
    handleDragOver, handleDragEnter, handleDrop, handleDragLeave,
    handleDeleteMaterial, handleEditMaterialName, handleSaveMaterialName, handleCancelEditMaterial,
    handleMaterialPreview, handleClosePreview,
  };
}
